import React from "react";
import { usePermissions } from "../context/PermissionContext";
import AccessDenied from "../components/AccessDenied";
import LoadingSpinner from "../components/LoadingSpinner";

interface PermissionRouteProps {
  permissionKey: string; // The name of the permission to check (e.g., "canManageRoles")
  element: React.ReactElement; // The element to render if permission is granted
  fallback?: React.ReactElement; // Optional custom fallback component
  // Optional detailed permission parameters
  detailedCheck?: {
    section: string; // e.g., "manage_roles"
    operation: string; // e.g., "view", "update", "assign", "delete"
    requiredValue?: string; // e.g., "all", "assigned" - if not provided, any non-empty value passes
  };
}

const PermissionRoute: React.FC<PermissionRouteProps> = ({
  permissionKey,
  element,
  fallback,
  detailedCheck,
}) => {
  const { permissions, loading, error } = usePermissions();
  // Show loading indicator if permissions are still loading
  if (loading) {
    return <LoadingSpinner />;
  }

  // If there was an error loading permissions, you might want to handle that
  if (error) {
    console.error("Error loading permissions:", error);
  }

  let hasPermission = false;

  // First check the basic boolean permission
  const basicPermission =
    permissions[permissionKey as keyof typeof permissions];

  // If detailed check is required, check that too
  if (detailedCheck) {
    const { section, operation, requiredValue } = detailedCheck;
    const detailedValue =
      permissions.detailedPermissions?.[
        section as keyof typeof permissions.detailedPermissions
      ]?.[
        operation as keyof typeof permissions.detailedPermissions[keyof typeof permissions.detailedPermissions]
      ];

    // If requiredValue is provided, check for exact match, otherwise any non-empty value is fine
    if (requiredValue) {
      hasPermission = detailedValue === requiredValue;
    } else {
      hasPermission = Boolean(detailedValue);
    }

  } else {
    // Just use the basic permission
    hasPermission = Boolean(basicPermission);
  }

  // Use default AccessDenied if no fallback is provided
  const defaultFallback = (
    <AccessDenied
      title={`${permissionKey.replace("can", "")} Access Restricted`}
      subTitle={`You don't have permission to access this feature.`}
    />
  );

  // If they have permission, render the element, otherwise show the fallback
  return hasPermission ? element : fallback || defaultFallback;
};

export default PermissionRoute;
